import {
    useState,
    useRef,
    useCallback,
    useEffect,
    useLayoutEffect,
} from 'react';
import {
    DEFAULT_PIP_ASPECT_RATIO,
    usePipPlayerStore,
} from '@/stores/pip-player.store';
import { PipEventContent } from './components/PipEventContent';
import { PipSourceContent } from './components/PipSourceContent';
import { ResizeEdge } from './components/ResizeEdge';
import { ResizeCorner } from './components/ResizeCorner';

const MIN_WIDTH = 320;
const MIN_HEIGHT = 180;
const DEFAULT_WIDTH = 721;
const DRAG_THRESHOLD = 4;
const COMPACT_WIDTH = 480;
const DEFAULT_MARGIN = 50;

function fitWidthToAspect(initialWidth: number, aspect: number) {
    let width = Math.min(initialWidth, window.innerWidth);
    let height = width / aspect;
    if (height > window.innerHeight) {
        height = window.innerHeight;
        width = height * aspect;
    }
    if (width < MIN_WIDTH) {
        width = MIN_WIDTH;
        height = width / aspect;
    }
    if (height < MIN_HEIGHT) {
        height = MIN_HEIGHT;
        width = height * aspect;
    }
    return { width, height };
}

type ResizeDirection =
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
}

function clampPosition(
    x: number,
    y: number,
    width: number,
    height: number,
): { x: number; y: number } {
    return {
        x: clamp(x, 0, window.innerWidth - width),
        y: clamp(y, 0, window.innerHeight - height),
    };
}

const INTERACTIVE_SELECTOR =
    'button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [data-radix-popper-content-wrapper]';

function isInteractiveElement(el: EventTarget | null): boolean {
    if (!(el instanceof Element)) return false;
    return el.closest(INTERACTIVE_SELECTOR) !== null;
}

function enableDragCursor(el: HTMLElement | null) {
    if (el) el.style.cursor = 'grabbing';
}

function disableDragCursor(el: HTMLElement | null) {
    if (el) el.style.cursor = '';
}

function applyPosition(el: HTMLElement, x: number, y: number) {
    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
}

function applySize(el: HTMLElement, width: number, height: number) {
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    if (width < COMPACT_WIDTH) {
        el.dataset.compact = '';
    } else {
        delete el.dataset.compact;
    }
}

const PIP_STORAGE_KEY = 'pip-player-bounds-v2';

interface PipBounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

function saveBounds(bounds: PipBounds) {
    try {
        sessionStorage.setItem(PIP_STORAGE_KEY, JSON.stringify(bounds));
    } catch {
        // quota exceeded — ignore
    }
}

function loadBounds(): PipBounds | null {
    try {
        const raw = sessionStorage.getItem(PIP_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PipBounds;
        const clamped = clampPosition(
            parsed.x,
            parsed.y,
            parsed.width,
            parsed.height,
        );
        return {
            ...clamped,
            width: clamp(parsed.width, MIN_WIDTH, window.innerWidth),
            height: clamp(parsed.height, MIN_HEIGHT, window.innerHeight),
        };
    } catch {
        return null;
    }
}

const defaultPos = (height: number) => ({
    x: DEFAULT_MARGIN,
    y: window.innerHeight - height - DEFAULT_MARGIN,
});

const defaultDim = (aspect: number) => fitWidthToAspect(DEFAULT_WIDTH, aspect);

export function PipPlayerHost() {
    const isOpen = usePipPlayerStore((s) => s.isOpen);
    const mode = usePipPlayerStore((s) => s.mode);
    const videoAspectRatio = usePipPlayerStore((s) => s.videoAspectRatio);
    const aspectRef = useRef(videoAspectRatio);
    aspectRef.current = videoAspectRatio;

    const containerRef = useRef<HTMLDivElement>(null);

    const initialDim = defaultDim(DEFAULT_PIP_ASPECT_RATIO);
    const pos = useRef(defaultPos(initialDim.height));
    const dim = useRef(initialDim);
    const rafId = useRef(0);
    const rafScheduled = useRef(false);
    const [, forceRender] = useState(0);

    const dragState = useRef<{
        startX: number;
        startY: number;
        startPosX: number;
        startPosY: number;
        dragging: boolean;
    } | null>(null);

    const resizeState = useRef<{
        direction: ResizeDirection;
        startX: number;
        startY: number;
        startWidth: number;
        startHeight: number;
        startPosX: number;
        startPosY: number;
    } | null>(null);

    const syncDOM = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        applyPosition(el, pos.current.x, pos.current.y);
        // Size is constant during a drag — skip it to avoid per-frame reflow of the video subtree.
        if (!dragState.current?.dragging) {
            applySize(el, dim.current.width, dim.current.height);
        }
    }, []);

    const scheduleSync = useCallback(() => {
        if (rafScheduled.current) return;
        rafScheduled.current = true;
        rafId.current = requestAnimationFrame(() => {
            rafScheduled.current = false;
            syncDOM();
        });
    }, [syncDOM]);

    useLayoutEffect(() => {
        if (!isOpen) return;
        const saved = loadBounds();
        const fallbackDim = defaultDim(aspectRef.current);
        pos.current = saved
            ? { x: saved.x, y: saved.y }
            : defaultPos(fallbackDim.height);
        dim.current = saved
            ? { width: saved.width, height: saved.height }
            : fallbackDim;
        forceRender((n) => n + 1);
        syncDOM();
    }, [isOpen, syncDOM]);

    useEffect(() => {
        const onPointerMove = (e: PointerEvent) => {
            if (dragState.current) {
                const dx = e.clientX - dragState.current.startX;
                const dy = e.clientY - dragState.current.startY;

                if (
                    !dragState.current.dragging &&
                    Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD
                ) {
                    return;
                }
                if (!dragState.current.dragging) {
                    // Re-anchor to the current point so crossing the threshold doesn't jump.
                    dragState.current.dragging = true;
                    dragState.current.startX = e.clientX;
                    dragState.current.startY = e.clientY;
                    enableDragCursor(containerRef.current);
                    return;
                }

                const desiredX =
                    dragState.current.startPosX +
                    (e.clientX - dragState.current.startX);
                const desiredY =
                    dragState.current.startPosY +
                    (e.clientY - dragState.current.startY);
                const next = clampPosition(
                    desiredX,
                    desiredY,
                    dim.current.width,
                    dim.current.height,
                );
                // Re-anchor the clamped axis so leaving an edge tracks the cursor
                // 1:1 — no accumulated out-of-bounds offset, no edge judder.
                if (next.x !== desiredX) {
                    dragState.current.startX = e.clientX;
                    dragState.current.startPosX = next.x;
                }
                if (next.y !== desiredY) {
                    dragState.current.startY = e.clientY;
                    dragState.current.startPosY = next.y;
                }
                pos.current = next;
                scheduleSync();
                return;
            }

            if (resizeState.current) {
                const {
                    direction,
                    startX,
                    startY,
                    startWidth,
                    startHeight,
                    startPosX,
                    startPosY,
                } = resizeState.current;
                const aspect = aspectRef.current;
                const dx = e.clientX - startX;
                const dy = e.clientY - startY;

                const heightDriven =
                    direction === 'top' || direction === 'bottom';

                let newWidth: number;
                let newHeight: number;
                if (heightDriven) {
                    newHeight =
                        direction === 'top'
                            ? startHeight - dy
                            : startHeight + dy;
                    newWidth = newHeight * aspect;
                } else {
                    newWidth = direction.includes('left')
                        ? startWidth - dx
                        : startWidth + dx;
                    newHeight = newWidth / aspect;
                }

                // Enforce minimums on both axes by promoting the limiting one.
                if (newWidth < MIN_WIDTH) {
                    newWidth = MIN_WIDTH;
                    newHeight = newWidth / aspect;
                }
                if (newHeight < MIN_HEIGHT) {
                    newHeight = MIN_HEIGHT;
                    newWidth = newHeight * aspect;
                }

                let newX = direction.includes('left')
                    ? startPosX + (startWidth - newWidth)
                    : startPosX;
                let newY = direction.includes('top')
                    ? startPosY + (startHeight - newHeight)
                    : startPosY;

                // Don't let the right/bottom edges escape the viewport.
                const maxWidth = window.innerWidth - newX;
                if (newWidth > maxWidth) {
                    newWidth = maxWidth;
                    newHeight = newWidth / aspect;
                    if (direction.includes('top')) {
                        newY = startPosY + (startHeight - newHeight);
                    }
                }
                const maxHeight = window.innerHeight - newY;
                if (newHeight > maxHeight) {
                    newHeight = maxHeight;
                    newWidth = newHeight * aspect;
                    if (direction.includes('left')) {
                        newX = startPosX + (startWidth - newWidth);
                    }
                }

                const clamped = clampPosition(newX, newY, newWidth, newHeight);
                pos.current = clamped;
                dim.current = { width: newWidth, height: newHeight };
                scheduleSync();
            }
        };

        const onPointerUp = () => {
            const wasDragging = dragState.current?.dragging;
            const wasResizing = !!resizeState.current;
            dragState.current = null;
            resizeState.current = null;
            disableDragCursor(containerRef.current);

            if (wasDragging || wasResizing) {
                saveBounds({ ...pos.current, ...dim.current });
            }
            if (wasDragging) {
                // Swallow the click the browser fires right after a drag so it
                // doesn't bubble to the player's play/pause toggle.
                const suppressClick = (clickEvent: MouseEvent) => {
                    clickEvent.stopPropagation();
                    clickEvent.preventDefault();
                };
                window.addEventListener('click', suppressClick, {
                    capture: true,
                });
                setTimeout(() => {
                    window.removeEventListener('click', suppressClick, {
                        capture: true,
                    });
                }, 0);
            }
        };

        // pointerrawupdate fires earlier and more frequently than pointermove,
        // giving the lowest input-to-paint latency so the player stays glued to the cursor.
        const moveEventName =
            'onpointerrawupdate' in window ? 'pointerrawupdate' : 'pointermove';
        window.addEventListener(moveEventName, onPointerMove as EventListener);
        window.addEventListener('pointerup', onPointerUp);
        return () => {
            window.removeEventListener(
                moveEventName,
                onPointerMove as EventListener,
            );
            window.removeEventListener('pointerup', onPointerUp);
            cancelAnimationFrame(rafId.current);
            rafScheduled.current = false;
            disableDragCursor(containerRef.current);
        };
    }, [scheduleSync]);

    const onContainerPointerDown = useCallback((e: React.PointerEvent) => {
        if (isInteractiveElement(e.target)) return;

        e.currentTarget.setPointerCapture(e.pointerId);
        dragState.current = {
            startX: e.clientX,
            startY: e.clientY,
            startPosX: pos.current.x,
            startPosY: pos.current.y,
            dragging: false,
        };
    }, []);

    const onResizePointerDown = useCallback(
        (direction: ResizeDirection) => (e: React.PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            resizeState.current = {
                direction,
                startX: e.clientX,
                startY: e.clientY,
                startWidth: dim.current.width,
                startHeight: dim.current.height,
                startPosX: pos.current.x,
                startPosY: pos.current.y,
            };
        },
        [],
    );

    const applyAspectRatio = useCallback(
        (aspect: number) => {
            const fitted = fitWidthToAspect(dim.current.width, aspect);
            dim.current = fitted;
            pos.current = clampPosition(
                pos.current.x,
                pos.current.y,
                fitted.width,
                fitted.height,
            );
            syncDOM();
        },
        [syncDOM],
    );

    useEffect(() => {
        if (!isOpen) return;
        const fitted = fitWidthToAspect(dim.current.width, videoAspectRatio);
        if (
            Math.abs(fitted.width - dim.current.width) < 0.5 &&
            Math.abs(fitted.height - dim.current.height) < 0.5
        ) {
            return;
        }
        applyAspectRatio(videoAspectRatio);
    }, [videoAspectRatio, isOpen, applyAspectRatio]);

    if (!isOpen) return null;

    return (
        <div
            ref={containerRef}
            className="group/host fixed left-0 top-0 z-[70] rounded-lg shadow-2xl select-none animate-in fade-in slide-in-from-bottom-2 duration-200 touch-none will-change-transform"
            style={{
                width: dim.current.width,
                height: dim.current.height,
                transform: `translate3d(${pos.current.x}px, ${pos.current.y}px, 0)`,
            }}
            onPointerDown={onContainerPointerDown}
        >
            <div className="w-full h-full overflow-hidden rounded-[2px] group/pip-inner">
                {mode === 'source' ? <PipSourceContent /> : <PipEventContent />}
            </div>

            <ResizeEdge direction="top" onStart={onResizePointerDown('top')} />
            <ResizeEdge
                direction="right"
                onStart={onResizePointerDown('right')}
            />
            <ResizeEdge
                direction="bottom"
                onStart={onResizePointerDown('bottom')}
            />
            <ResizeEdge
                direction="left"
                onStart={onResizePointerDown('left')}
            />
            <ResizeCorner
                direction="top-left"
                onStart={onResizePointerDown('top-left')}
            />
            <ResizeCorner
                direction="top-right"
                onStart={onResizePointerDown('top-right')}
            />
            <ResizeCorner
                direction="bottom-left"
                onStart={onResizePointerDown('bottom-left')}
            />
            <ResizeCorner
                direction="bottom-right"
                onStart={onResizePointerDown('bottom-right')}
            />
        </div>
    );
}
